import styled from '@emotion/styled';

/**
 * FutureStoriesPage — Pages 9-11 of the Kuutio print report.
 *
 * Large scenario heading + intro text + numbered future vision story cards.
 * Reusable template for each scenario's future visions.
 *
 * Page 9 (Demokratia): IDML Spread_ud8.xml
 *   u540: Pikkuotsikko "Osallistujien tekemät tulevaisuuskuvat"
 *   u467f: Intro text
 *   u7b1c: Scenario name "Demokratia"
 *   u2d4c: Story card 1 (black bg)
 *   u7a4f: Story card 2
 *   u2d53/u2d6b: Number badges (1., 2.)
 *
 * Page 10 (Väestö): IDML Spread_u7a7d.xml — same layout with 3 columns (3., 4., 5.)
 */
export default function FutureStoriesPage({
  pageNumber,
  scenarioName,
  scenarioColor,
  subtitle = 'Osallistujien tekemät tulevaisuuskuvat',
  introText,
  stories, // [{ number, title, body }]
  instanceNumber = '3',
  topic = 'Kansainvälisten opiskelijoiden työllistyminen Suomeen',
}) {
  // Column layouts: positions and widths for 1, 2, or 3 columns
  const columnLayouts = {
    1: [{ left: '42pt', width: '500pt' }],
    2: [
      { left: '42pt', width: '360pt' },
      { left: '430pt', width: '360pt' },
    ],
    3: [
      { left: '42pt', width: '240pt' },
      { left: '300pt', width: '230pt' },
      { left: '548pt', width: '255pt' },
    ],
  };

  const cols = columnLayouts[Math.min(stories.length, 3)] || columnLayouts[3];
  const cardTop = stories.length <= 2 ? '240.9pt' : '235.7pt';

  return (
    <Page className="page">
      {/* Subtitle: Pikkuotsikko */}
      <SubTitle>{subtitle}</SubTitle>
      <YellowRule />

      {introText && <IntroText>{introText}</IntroText>}

      {/* Large scenario heading */}
      <ScenarioHeading $color={scenarioColor}>{scenarioName}</ScenarioHeading>

      {/* Story cards — auto-layout based on count */}
      {stories.map((story, i) => (
        <StoryCard key={i} style={{ left: cols[i].left, top: cardTop, width: cols[i].width }}>
          <NumberBadge><span>{story.number}</span></NumberBadge>
          <StoryTitle>{story.title}</StoryTitle>
          <StoryBody>{story.body}</StoryBody>
        </StoryCard>
      ))}

      <PageFooter>
        <FooterLeft><strong>Kuutio #{instanceNumber}:</strong> {topic}</FooterLeft>
        <FooterRight>{pageNumber}</FooterRight>
      </PageFooter>
    </Page>
  );
}

const Page = styled.div`
  width: 297mm;
  min-height: 210mm;
  background: #fff;
  color: #000;
  position: relative;
  font-family: 'Rational', sans-serif;
  box-sizing: border-box;
  overflow: hidden;
  @media print { break-after: page; }
`;

const SubTitle = styled.h2`
  position: absolute;
  left: 42.1pt;
  top: 33.7pt;
  width: 441.2pt;
  font-size: 18pt;
  font-weight: 400;
  font-style: italic;
  margin: 0;
`;

const YellowRule = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 56pt;
  width: calc(297mm - 42.5pt - 28.3pt);
  height: 3pt;
  background: #FBE300;
`;

const IntroText = styled.p`
  position: absolute;
  left: 42.0pt;
  top: 63.2pt;
  width: 451.7pt;
  font-size: 11pt;
  line-height: 13pt;
`;

/* IDML: Black 48pt in 49.6pt frame, AllCaps, scenario color */
const ScenarioHeading = styled.h1`
  position: absolute;
  left: 42.0pt;
  top: 159.3pt;
  height: 49.6pt;
  font-size: 42pt;
  font-weight: 900;
  font-style: normal;
  text-transform: uppercase;
  line-height: 49.6pt;
  margin: 0;
  color: ${p => p.$color};
`;

const StoryCard = styled.div`
  position: absolute;
  background: transparent;
  color: #000;
  padding: 4pt;
`;

const NumberBadge = styled.div`
  width: 21.9pt;
  height: 21.9pt;
  border-radius: 50%;
  background: #000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10pt;
  font-weight: 700;
  margin-bottom: 8pt;
`;

/* IDML: Bold 12pt */
const StoryTitle = styled.h3`
  font-size: 12pt;
  font-weight: 700;
  line-height: 14pt;
  margin: 0 0 8pt 0;
`;

/* IDML: Book 10pt, 12pt leading */
const StoryBody = styled.p`
  font-size: 10pt;
  font-weight: 400;
  line-height: 12pt;
  margin: 0 0 7pt 0;
  white-space: pre-line;
`;

const PageFooter = styled.div`
  position: absolute;
  bottom: 14pt;
  left: 42.5pt;
  right: 28.3pt;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 0.5pt solid rgba(0, 0, 0, 0.3);
  padding-top: 6pt;
`;
const FooterLeft = styled.span` font-size: 8pt; font-style: italic; `;
const FooterRight = styled.span` font-size: 10pt; `;
